import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useLoadProject, useProject } from "../store/projectContext";
import { useVideoConfig } from "../components/use-video-config";
import { ScenePlayer } from "../components/ScenePlayer/ScenePlayer";
import Player from "../components/Player";
import PlayerProvider from "../components/PlayerProvider";
import { delayRender, continueRender } from "../components/delay-render";
import { SubtitleOverlay } from "../components/Subtitles";
import { TextOverlayRenderer } from "../components/TextOverlays";
import WatermarkOverlay from "../components/WatermarkOverlay";

const API_BASE        = process.env.REACT_APP_API_BASE        || "http://localhost:3001";
// const STORY_API_BASE  = process.env.REACT_APP_STORY_API_BASE  || "https://dev.steve.ai";
const STORY_API_BASE = 'https://app.steve.ai';

/**
 * Fetch project JSON from the storyteller API.
 * Mirrors getStory() in steve-prompt-script.
 */
async function getStory(projectId) {
  const formData = new FormData();
  formData.append("project_id", projectId);

  const res = await fetch(`${STORY_API_BASE}/steveai/storyteller/get_story/`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) throw new Error(`get_story ${res.status}: ${await res.text()}`);

  const json = await res.json();
  if (!json.success) throw new Error(json.message || "get_story returned success=false");

  // The API wraps the project under `final_project_json` or returns it directly
  return json.data?.final_project_json || json.data;
}

// ─── Block renderer until project is loaded ───────────────────────────────────
// delayRender() at module-load time keeps window.ready = false immediately,
// so the puppeteer renderer waits before capturing any frame.
let moduleLoadHandle = null;
if (typeof window !== "undefined" &&
    (window.location.pathname.startsWith("/render") ||
     window.location.pathname.includes("/export"))) {
  moduleLoadHandle = delayRender("ExportPage: loading project data");
}

// ─── Visible composition ──────────────────────────────────────────────────────
const ExportContent = () => {
  const videoConfig = useVideoConfig();
  const project = useProject();
  // When plainVideo is set (top-level preview/download render), skip subtitles
  // and text overlays entirely. Transitions are already neutralized in the
  // project data (scene.transition = { effect: 'none' }).
  const plainVideo = project?.plainVideo === true;

  return (
    <div style={{
      width: "100vw",
      height: "100vh",
      overflow: "hidden",
      background: "#000",
      position: "relative",
    }}>
      <ScenePlayer />
      {!plainVideo && <SubtitleOverlay />}
      {!plainVideo && <TextOverlayRenderer />}
      {/* Free-plan watermark — mounted only for free users. The render server
          sets project.watermark.enabled from the plan (resolveWatermark in
          server/routes/render.js); paid plans don't mount the component at all.
          Rendered on EVERY export, including plainVideo downloads. */}
      {project?.watermark?.enabled && <WatermarkOverlay />}
      {/*
        Hidden Player mounts the frame-clock component.
        The renderer calls window.picmaker_setFrame(n) — registered in
        PlayerProvider — which updates TimelineContext.frame so videos
        seek frame-accurately before each screenshot.
      */}
      <div style={{ display: "none" }}>
        <Player videoConfig={videoConfig} />
      </div>
    </div>
  );
};

// ─── Export page ──────────────────────────────────────────────────────────────
const ExportPage = () => {
  // /render?id=:projectId&e=:exportId  (renderer path)
  // /storyteller/:projectId/editor/export?e=:exportId  (nested path)
  const { projectId: routeProjectId } = useParams();
  const [searchParams] = useSearchParams();
  const projectId = routeProjectId || searchParams.get("id");
  const exportId  = searchParams.get("e");

  const loadProject    = useLoadProject();
  const project        = useProject();
  const [ready, setReady] = useState(false);
  const loadHandleRef  = useRef(moduleLoadHandle);

  useEffect(() => {
    if (!projectId) return;

    (async () => {
      let data = null;

      // 1. When the render server is driving puppeteer it pre-downloads all
      //    media and rewrites URLs to local /api/media/* paths.
      //    Fetch that version first for fast, reliable rendering.
      if (exportId) {
        try {
          const r = await fetch(`${API_BASE}/api/render/${exportId}/project`);
          if (r.ok) {
            data = await r.json();
            console.log("[ExportPage] using local-media project from render server");
          }
        } catch (e) {
          console.warn("[ExportPage] render project not ready, falling back", e);
        }
      }

      // 2. Fall back to the storyteller API
      if (!data) {
        console.log("[ExportPage] fetching via getStory:", projectId);
        data = await getStory(projectId);
      }

      loadProject(data);
      setReady(true);
    })().catch((err) => {
      console.error("[ExportPage] failed to load project:", err);
      setReady(true); // unblock renderer — better a blank frame than a hung process
    });
  }, [projectId, exportId, loadProject]);

  // Release delayRender handle once project is in state.
  // setTimeout(0) instead of requestAnimationFrame: rAF is throttled to ~1fps
  // in headless Chromium background tabs, causing 58s timeouts with concurrent tabs.
  useEffect(() => {
    if (!ready || loadHandleRef.current === null) return;
    setTimeout(() => {
      if (loadHandleRef.current !== null) {
        continueRender(loadHandleRef.current);
        loadHandleRef.current = null;
      }
    }, 0);
  }, [ready]);

  if (!ready || !project) return null;

  return (
    <PlayerProvider>
      <ExportContent />
    </PlayerProvider>
  );
};

export default ExportPage;
